import { useEffect, useMemo, useState } from 'react';
import { addDays, addYears, startOfWeek, parseISO } from 'date-fns';
import { formatDateShort, toApiDate, toHHMM } from '../lib/dateFormat';
import { lehrerTimetableApi, type TeacherTimetableItem } from '../lib/lehrer';
import { fetchTrials, stehtImTerminplan, type TrialLessonDTO } from '../lib/trials';
import { unterrichtseinheitenApi, type UnterrichtseinheitDTO } from '../lib/unterrichtseinheiten';
import { migrationApi, type MigrationPreviewEvent } from '../lib/api/migration';

export interface TimetableEvent {
  id: string;
  type: 'unit' | 'trial' | 'pattern' | 'migration';
  dayOfWeek: number; // 1..7
  date: string; // YYYY-MM-DD
  startTime: string; // HH:MM
  endTime: string;   // HH:MM
  color: string;
  title?: string;
  subtitle?: string;
  roomName?: string | null;
  fachName?: string | null;
  participants?: string | null;
  /** Teilnehmer als Datensätze — das Kontextmenü braucht mehr als die Namensliste. */
  participantList?: Array<{ subprofileId: string; personId: number; wpUserId: number; subIndex: number; name: string }> | null;
  hint?: string | null;
  startsAt?: string | null; // Kursbeginn laut Regel (YYYY-MM-DD)
  courseId?: number | null;
  isPlaceholder?: boolean;
  migrationCaseId?: number;
}

export interface UseTeacherTimetableResult {
  weekStart: Date;
  pattern: TeacherTimetableItem[];
  units: UnterrichtseinheitDTO[];
  trials: TrialLessonDTO[];
  migrationPreview: MigrationPreviewEvent[];
  events: TimetableEvent[];
  occupiedMap: Map<string, boolean>; // key: `${date} ${HH:MM}` → true=belegt
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

function toYmd(d: Date): string {
  return toApiDate(d);
}

export function useTeacherTimetable(teacherId: number | string | undefined, referenceDate: Date, standortId?: number | null): UseTeacherTimetableResult {
  const [pattern, setPattern] = useState<TeacherTimetableItem[]>([]);
  const [units, setUnits] = useState<UnterrichtseinheitDTO[]>([]);
  const [trials, setTrials] = useState<TrialLessonDTO[]>([]);
  const [migrationPreview, setMigrationPreview] = useState<MigrationPreviewEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Woche basiert auf referenceDate (für Kalenderansicht / Navigation)
  const weekStart = useMemo(() => startOfWeek(referenceDate, { weekStartsOn: 1 }), [referenceDate]);
  const start = useMemo(() => toYmd(weekStart), [weekStart]);
  const end = useMemo(() => toYmd(addDays(weekStart, 6)), [weekStart]);
  // Stundenplan ist zukunftsrelativ: Trials ab heute bis weit in die Zukunft laden.
  const trialsStart = useMemo(() => toYmd(new Date()), []);
  const trialsEnd = useMemo(() => toYmd(addYears(new Date(), 20)), []);

  useEffect(() => {
    const load = async () => {
      if (!teacherId) {
        setPattern([]);
        setUnits([]);
        setTrials([]);
        setMigrationPreview([]);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const teacherIdNumber = Number(teacherId) || 0;
        const [ptn, unitItems, trialItems, previewItems] = await Promise.all([
          lehrerTimetableApi.getTimetable(teacherId, toYmd(referenceDate)),
          unterrichtseinheitenApi.list({ teacherId, standortId: standortId ?? undefined, start, end }),
          fetchTrials(trialsStart, trialsEnd, standortId ?? undefined),
          standortId && teacherIdNumber > 0
            ? migrationApi.getPreviewEvents(standortId, teacherIdNumber).catch(() => [])
            : Promise.resolve([] as MigrationPreviewEvent[]),
        ]);
        setPattern(ptn || []);
        setUnits(unitItems || []);
        const sameTeacherTrials = (trialItems || []).filter(t => String(t.teacherId || '') === String(teacherId));
        setTrials(sameTeacherTrials);
        setMigrationPreview(previewItems || []);
      } catch (e) {
        setError(e instanceof Error ? e : new Error('Unbekannter Fehler'));
        setPattern([]);
        setUnits([]);
        setTrials([]);
        setMigrationPreview([]);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [teacherId, standortId, start, end, trialsStart, trialsEnd, referenceDate]);

  const events: TimetableEvent[] = useMemo(() => {
    const unitEvents: TimetableEvent[] = units.map(u => {
      const d = new Date(u.start_zeit);
      const dow = ((d.getDay() + 6) % 7) + 1;
      const color = u.status === 'abgesagt'
        ? 'bg-rose-200 border-rose-500 text-rose-950'
        : (u.source_type === 'trial' ? 'bg-emerald-200 border-emerald-600 text-emerald-950' : (u.kurs_farbe || 'bg-sky-200 border-sky-600 text-sky-950'));
      const startTime = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      const e = new Date(u.end_zeit);
      const endTime = `${String(e.getHours()).padStart(2, '0')}:${String(e.getMinutes()).padStart(2, '0')}`;
      return {
        id: `unit-${u.id}`,
        type: 'unit',
        dayOfWeek: dow,
        date: toApiDate(d),
        startTime,
        endTime,
        color,
        title: u.kurs_name || u.fach_name || 'Unterricht',
        subtitle: u.teilnehmer_namen || undefined,
        roomName: u.raum_name || null,
        fachName: u.fach_name || null,
        participants: u.teilnehmer_namen || null,
        hint: null,
        courseId: u.course_id ?? null,
        isPlaceholder: Number(u.kurs_is_placeholder ?? 0) === 1,
      };
    });
    const trialEvents: TimetableEvent[] = trials.filter(stehtImTerminplan).map(t => {
      const ymd = t.date;
      const d = parseISO(`${ymd}T${t.startTime}`);
      const dow = ((d.getDay() + 6) % 7) + 1;
      const startTime = toHHMM(t.startTime);
      const endTime = toHHMM(t.endTime);
      return {
        id: `trial-${t.id}`,
        type: 'trial',
        dayOfWeek: dow,
        date: ymd,
        startTime,
        endTime,
        color: 'bg-emerald-200 border-emerald-600 text-emerald-950',
        // „Probestunde" als Überschrift, das Fach daneben, der Name darunter —
        // vorher stand dort nur Zeit und Datum, der Schüler fehlte (T-0028).
        title: 'Probestunde',
        subtitle: t.studentName,
        roomName: t.roomName || null,
        fachName: t.instrument || null,
        participants: t.studentName || null,
        hint: null,
      };
    });
    const todayYmd = toYmd(new Date());
    const concreteCourseDates = new Set(
      unitEvents
        .filter((event) => event.courseId != null)
        .map((event) => `${event.courseId}:${event.date}`),
    );
    const patternEvents: TimetableEvent[] = pattern.flatMap(p => {
      // Filter: endet vor heute => nicht anzeigen
      if (p.endsAt && p.endsAt < todayYmd) return [];
      const startTime = toHHMM(p.time_start);
      const endTime = toHHMM(p.time_end);
      const occurrenceDate = toYmd(addDays(weekStart, Math.min(6, Math.max(0, p.dayOfWeek - 1))));
      if (concreteCourseDates.has(`${p.course_id}:${occurrenceDate}`)) return [];
      // Alle n Wochen: nur in jeder n-ten Woche ab der Woche des Kursbeginns —
      // bisher stand ein Zwei-Wochen-Kurs jede Woche im Plan.
      const intervall = Math.max(1, Number(p.intervall ?? 1) || 1);
      if (intervall > 1 && p.startsAt) {
        const beginn = parseISO(p.startsAt);
        const beginnWoche = addDays(beginn, -((beginn.getDay() + 6) % 7));
        const wochen = Math.round((weekStart.getTime() - beginnWoche.getTime()) / (7 * 24 * 60 * 60 * 1000));
        if (((wochen % intervall) + intervall) % intervall !== 0) return [];
      }
      let hint: string | null = null;
      if (p.startsAt && p.startsAt > todayYmd) {
        hint = `Start ab ${formatDateShort(p.startsAt)}`;
      } else if (p.endsAt && p.endsAt >= todayYmd) {
        hint = `Bis zum ${formatDateShort(p.endsAt)}`;
      }
      // Analog zum Unit-Pattern (Kalender): CourseBlock rendert oben
      // „HH:MM-HH:MM | fachName" + darunter participant-Preview.
      // title/subtitle landen v.a. im Tooltip — orientieren uns am Unit-Mapping.
      const fachName = (p.fach_name || '').trim() || null;
      const roomName = (p.room_name || '').trim() || null;
      const participantNames = (p.participantNames || '').trim();
      return [{
        id: `pattern-${p.course_id}-${p.dayOfWeek}-${startTime}`,
        type: 'pattern',
        dayOfWeek: p.dayOfWeek,
        date: occurrenceDate,
        startTime,
        endTime,
        color: hint ? 'bg-violet-200 border-violet-600 text-violet-950' : 'bg-sky-100 border-sky-500 text-sky-950',
        title: p.course_name || fachName || 'Kurs',
        subtitle: participantNames || undefined,
        roomName,
        fachName,
        participants: participantNames || null,
        participantList: Array.isArray(p.participants) ? p.participants : null,
        hint,
        startsAt: p.startsAt ?? null,
        courseId: p.course_id,
        isPlaceholder: Boolean(p.is_placeholder),
      }];
    });
    const migrationEvents: TimetableEvent[] = migrationPreview.map(m => {
      const startTime = toHHMM(m.start_time);
      const endTime = toHHMM(m.end_time);
      const subtitleParts = [m.student_name, m.uform_name].filter((v): v is string => !!v && v.trim() !== '');
      return {
        id: `migration-${m.case_id}`,
        type: 'migration',
        dayOfWeek: m.day_of_week,
        date: todayYmd,
        startTime,
        endTime,
        color: 'bg-amber-100 border-amber-500 text-amber-950 border-dashed',
        title: m.fach_name || 'Migration',
        subtitle: subtitleParts.join(' · ') || undefined,
        roomName: null,
        fachName: m.fach_name || null,
        participants: m.student_name || null,
        hint: 'Migrations-Vorschau',
        courseId: null,
        isPlaceholder: true,
        migrationCaseId: m.case_id,
      };
    });
    return [...patternEvents, ...unitEvents, ...trialEvents, ...migrationEvents];
  }, [pattern, units, trials, migrationPreview, weekStart]);

  const occupiedMap = useMemo(() => {
    const map = new Map<string, boolean>();
    const addRange = (ymd: string, s: string, e: string) => {
      const [sh, sm] = s.split(':').map(Number);
      const [eh, em] = e.split(':').map(Number);
      const startMinutes = sh * 60 + sm;
      const endMinutes = eh * 60 + em;
      for (let m = startMinutes; m < endMinutes; m += 15) {
        const hh = String(Math.floor(m / 60)).padStart(2, '0');
        const mm = String(m % 60).padStart(2, '0');
        map.set(`${ymd} ${hh}:${mm}`, true);
      }
    };
    events.forEach(ev => {
      // Migrations-Vorschau ist soft — nicht als belegt markieren, sonst werden
      // echte Buchungen (Probestunden, Anmeldungen) im Slot blockiert obwohl
      // der Migrations-Case noch nicht bestätigt ist.
      if (ev.type === 'migration') return;
      addRange(ev.date, ev.startTime, ev.endTime);
    });
    return map;
  }, [events]);

  const refetch = async () => {
    if (!teacherId) return;
    try {
      const teacherIdNumber = Number(teacherId) || 0;
      const [ptn, unitItems, trialItems, previewItems] = await Promise.all([
        lehrerTimetableApi.getTimetable(teacherId, toYmd(referenceDate)),
        unterrichtseinheitenApi.list({ teacherId, standortId: standortId ?? undefined, start, end }),
        fetchTrials(trialsStart, trialsEnd, standortId ?? undefined),
        standortId && teacherIdNumber > 0
          ? migrationApi.getPreviewEvents(standortId, teacherIdNumber).catch(() => [])
          : Promise.resolve([] as MigrationPreviewEvent[]),
      ]);
      setPattern(ptn || []);
      setUnits(unitItems || []);
      const sameTeacherTrials = (trialItems || []).filter(t => String(t.teacherId || '') === String(teacherId));
      setTrials(sameTeacherTrials);
      setMigrationPreview(previewItems || []);
    } catch (e) {
      // Fehler still halten; Konsistenz behalten
    }
  };

  return { weekStart, pattern, units, trials, migrationPreview, events, occupiedMap, isLoading, error, refetch };
}


